import { Link } from "react-router"
import { useTranslation } from "react-i18next"

import type { Device } from "@/api/generated/model"
import { OnlineDot } from "@/components/kit/OnlineDot"
import { EmptyState } from "@/components/kit/states"
import { Button } from "@/components/ui/button"
import { formatDateTime, formatRelative } from "@/lib/datetime"
import { formatCount, localeOf } from "@/lib/format"
import { topDevices } from "../lib/overview"

/** FR-DSH-OVW-05: the five busiest phones, online or not, and when each was last heard from. */
export function TopDevices({ items }: { items: Device[] }) {
  const { t, i18n } = useTranslation("overview")
  const locale = localeOf(i18n.language)
  if (items.length === 0) {
    return (
      <EmptyState
        title={t("devices.empty")}
        body={t("devices.emptyBody")}
        action={
          <Button asChild size="sm">
            <Link to="/devices">{t("devices.add")}</Link>
          </Button>
        }
      />
    )
  }
  return (
    <ul className="divide-y divide-border">
      {topDevices(items, 5).map((d) => (
        <li key={d.id} className="flex items-start gap-3 py-2.5">
          <OnlineDot online={d.online} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <Link
                to={`/devices/${d.id}`}
                className="min-w-0 truncate text-sm font-medium hover:underline"
              >
                {d.name ?? d.model ?? d.id}
              </Link>
              <span className="numeric text-xs text-muted-foreground">
                {t("devices.traffic", {
                  sent: formatCount(d.sentSMSCount, locale),
                  received: formatCount(d.receivedSMSCount, locale),
                })}
              </span>
            </div>
            <p className="truncate text-xs text-muted-foreground">
              {d.lastHeartbeat ? (
                <time dateTime={d.lastHeartbeat} title={formatDateTime(d.lastHeartbeat, locale)}>
                  {t("devices.lastSeen", { when: formatRelative(d.lastHeartbeat, locale) })}
                </time>
              ) : (
                t("devices.never")
              )}
            </p>
          </div>
        </li>
      ))}
    </ul>
  )
}
